import React, { useRef } from "react";
import { UploadedFile } from "../../types";
import { FileIcon, defaultStyles } from "react-file-icon";
import useDeviceType from "../../hooks/useDeviceType";
import { Eyebrow } from "../atoms/eyebrow";
import { DisplayHeading } from "../atoms/display-heading";
import { IconButton } from "../atoms/icon-button";

interface FileManagerProps {
	files: Map<string, UploadedFile>;
	onFileUpload: (files: FileList) => void;
	onRemoveFile: (fileName: string) => void;
	onClearAll: () => void;
	onViewFile?: (fileName: string, content: string) => void;
	title?: React.ReactNode;
	isCollapsed?: boolean;
	onToggleCollapse?: (collapsed: boolean) => void;
	isEmbedded?: boolean;
}

const FileManager: React.FC<FileManagerProps> = ({
	files,
	onFileUpload,
	onRemoveFile,
	onClearAll,
	onViewFile,
	title = (
		<div className="flex items-center gap-1">
			<svg
				className="w-5 h-5"
				fill="none"
				stroke="currentColor"
				viewBox="0 0 24 24"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="2"
					d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
				/>
			</svg>
			Files
		</div>
	),
	isCollapsed = false,
	onToggleCollapse,
	isEmbedded = false,
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { isMobile, isDesktop } = useDeviceType();

	const handleUploadClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			onFileUpload(event.target.files);
			event.target.value = "";
		}
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return "0 B";
		const k = 1024;
		const sizes = ["B", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
	};

	const getFileTypeIcon = (fileName: string) => {
		const ext = fileName.split(".").pop()?.toLowerCase() || "";

		// Use react-file-icon with custom styling
		return (
			<div className="w-4 h-4 flex items-center justify-center">
				<FileIcon
					extension={ext || "txt"}
					{...(defaultStyles[ext] || defaultStyles.txt)}
					size={16}
				/>
			</div>
		);
	};

	const isPythonFile = (fileName: string) => {
		const ext = fileName.split(".").pop()?.toLowerCase();
		return ext === "py";
	};

	const handleViewFile = (fileName: string, fileData: UploadedFile) => {
		if (!onViewFile || !isPythonFile(fileName)) return;

		try {
			// Convert Uint8Array to string
			const decoder = new TextDecoder("utf-8");
			const content = decoder.decode(fileData.data);
			onViewFile(fileName, content);
		} catch (error) {
			console.error(`Failed to read file ${fileName}:`, error);
		}
	};

	return (
		<div
			className={`${isEmbedded ? "h-full flex flex-col" : `h-full max-h-full flex flex-col transition-all duration-300 ${isCollapsed ? "w-0 overflow-hidden" : "w-full bg-white dark:bg-gray-800"}`}`}
		>
			{(!isCollapsed || isEmbedded) && (
				<>
					{/* Header - Only show when not embedded since SidePane handles it */}
					{!isEmbedded && (
						<div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
							<div className="flex items-center gap-1 min-w-0 flex-1">
								<h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300 truncate">
									{title}
								</h2>
								{files.size > 0 && (
									<span className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs px-1 py-0.5 rounded-md font-medium flex-shrink-0">
										{files.size}
									</span>
								)}
							</div>

							{files.size > 0 && (
								<div className="relative group">
									<button
										onClick={onClearAll}
										className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
									>
										<svg
											className="w-4 h-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
											></path>
										</svg>
									</button>
									{/* Tooltip only on desktop */}
									{isDesktop && (
										<div className="absolute -bottom-8 right-0 bg-gray-900 text-white px-2 py-1 rounded-lg text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg z-50">
											Delete all files
											<div className="absolute bottom-full right-2 w-0 h-0 border-l-2 border-r-2 border-b-2 border-transparent border-b-gray-900"></div>
										</div>
									)}
								</div>
							)}
						</div>
					)}

					{/* Embedded Header - Simple header for embedded mode */}
					{isEmbedded && (
						<div className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-200/80 dark:border-white/[0.06]">
							<div className="flex items-baseline gap-2 min-w-0 flex-1">
								<DisplayHeading size="md">Files</DisplayHeading>
								{files.size > 0 && (
									<Eyebrow className="!text-[9px] !tracking-[0.18em]">
										{files.size}
									</Eyebrow>
								)}
							</div>

							{files.size > 0 && (
								<div className="relative group">
									<IconButton
										size="xs"
										tone="danger"
										onClick={onClearAll}
										title="Delete all files"
									>
										<svg
											className="w-3.5 h-3.5"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.7"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
											/>
										</svg>
									</IconButton>
								</div>
							)}
						</div>
					)}

					{/* File List - Scrollable Area */}
					<div className="flex-1 overflow-y-auto min-h-0 max-h-full">
						{files.size === 0 ? (
							<div className="h-full flex items-center justify-center p-4">
								<div className="text-center max-w-[180px]">
									<div className="w-9 h-9 mx-auto mb-2 rounded-md border border-dashed border-gray-300 dark:border-white/[0.1] flex items-center justify-center">
										<svg
											className="w-4 h-4 text-gray-400 dark:text-gray-600"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
											/>
										</svg>
									</div>
									<p className="text-gray-700 dark:text-gray-300 text-[12px] font-medium">
										No files yet
									</p>
									<p className="text-gray-500 dark:text-gray-500 text-[11px] mt-0.5 leading-snug">
										Drop or upload files to use in your Python code.
									</p>
								</div>
							</div>
						) : (
							<div className="py-1.5">
								{Array.from(files.entries()).map(([fileName, fileData]) => (
									<div
										key={fileName}
										className="group flex items-center gap-2 px-3 py-1.5 mx-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
									>
										<div className="flex-shrink-0">
											{getFileTypeIcon(fileName)}
										</div>
										<div className="flex-1 min-w-0">
											<div
												className="text-[12px] text-gray-800 dark:text-gray-200 truncate font-medium leading-tight"
												title={fileName}
											>
												{fileName}
											</div>
											<div className="text-[10px] tabular text-gray-500 dark:text-gray-500 mt-0.5">
												{formatFileSize(fileData.size)}
											</div>
										</div>
										<div className="flex items-center gap-0.5">
											{isPythonFile(fileName) && onViewFile && (
												<IconButton
													size="xs"
													tone="accent"
													className="opacity-0 group-hover:opacity-100"
													onClick={() => handleViewFile(fileName, fileData)}
													title="Open in editor"
												>
													<svg
														className="w-3 h-3"
														fill="none"
														stroke="currentColor"
														strokeWidth="1.7"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
														/>
													</svg>
												</IconButton>
											)}
											<IconButton
												size="xs"
												tone="danger"
												className="opacity-0 group-hover:opacity-100"
												onClick={() => onRemoveFile(fileName)}
												title="Remove file"
											>
												<svg
													className="w-3 h-3"
													fill="none"
													stroke="currentColor"
													strokeWidth="1.7"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M6 18L18 6M6 6l12 12"
													/>
												</svg>
											</IconButton>
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Upload Area - Always Visible at Bottom */}
					<div className="flex-shrink-0 border-t border-gray-200/80 dark:border-white/[0.06] p-2">
						<input
							ref={fileInputRef}
							type="file"
							multiple
							className="hidden"
							accept="*/*"
							onChange={handleFileChange}
						/>
						<button
							onClick={handleUploadClick}
							className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md
							           border border-dashed border-gray-300 dark:border-white/[0.1]
							           text-gray-600 dark:text-gray-400
							           hover:text-orange-700 dark:hover:text-amber-300
							           hover:border-orange-400 dark:hover:border-amber-400/40
							           hover:bg-orange-50/60 dark:hover:bg-amber-400/[0.04]
							           transition-colors duration-150"
							title="Add files to your Python environment"
						>
							<svg
								className="w-3 h-3"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M12 4v16m8-8l-8-8-8 8"
								/>
							</svg>
							<span className="text-[11px] font-medium tracking-tight">
								Upload files
							</span>
							{!isMobile && (
								<span className="text-[10px] text-gray-400 dark:text-gray-600 tracking-tight">
									· or drop here
								</span>
							)}
						</button>
					</div>
				</>
			)}
		</div>
	);
};

export default FileManager;
